import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, BACKEND_URL } from '@/App';
import { useAuth } from '@/context/AuthContext';
import {
  ArrowLeft, Bike, Trophy, Medal, Crown, Zap, Users, Target, Gauge, TrendingUp, Mars, Venus
} from 'lucide-react';
import { memberColor, memberInitials } from '@/utils/memberColor';

function formatSegundos(seg) {
  if (!seg || seg <= 0) return '—';
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = Math.floor(seg % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatKm(km) {
  if (km == null) return '—';
  return Number(km).toFixed(2).replace(/\.00$/, '') + ' km';
}

// === PODIO TIPO OLIMPIADA ===
const PODIUM_HEIGHTS = { 0: 160, 1: 120, 2: 96 };
const PODIUM_AVATAR_SIZES = { 0: 116, 1: 92, 2: 84 };
const PODIUM_THEMES = {
  0: {
    name: 'oro',
    ring: '#fde047',
    glow: 'rgba(253,224,71,0.7)',
    pedestalGrad: 'linear-gradient(180deg, #fde047 0%, #f59e0b 60%, #b45309 100%)',
    pedestalText: '#3b2a00',
    crownIcon: Crown,
    label: '1',
  },
  1: {
    name: 'plata',
    ring: '#e5e7eb',
    glow: 'rgba(229,231,235,0.6)',
    pedestalGrad: 'linear-gradient(180deg, #f3f4f6 0%, #9ca3af 60%, #4b5563 100%)',
    pedestalText: '#1f2937',
    crownIcon: Medal,
    label: '2',
  },
  2: {
    name: 'bronce',
    ring: '#fdba74',
    glow: 'rgba(253,186,116,0.55)',
    pedestalGrad: 'linear-gradient(180deg, #fdba74 0%, #c2773c 60%, #7c2d12 100%)',
    pedestalText: '#1f0f00',
    crownIcon: Medal,
    label: '3',
  },
};

function PodiumEntry({ entry, place }) {
  const theme = PODIUM_THEMES[place];
  const color = memberColor(entry.user_id);
  const Icon = theme.crownIcon;
  const avatarSize = PODIUM_AVATAR_SIZES[place];
  const pedestalHeight = PODIUM_HEIGHTS[place];

  return (
    <div className="flex flex-col items-center" data-testid={`podium-${place + 1}`}>
      {/* Nombre arriba */}
      <p className="text-xs sm:text-sm font-extrabold text-white text-center mb-2 truncate max-w-[130px] uppercase tracking-wider">
        {entry.user_name}
      </p>

      {/* Avatar con halo */}
      <div className="relative mb-3">
        {place === 0 && (
          <Crown
            className="absolute -top-7 left-1/2 -translate-x-1/2 w-8 h-8 text-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.9)]"
            style={{ animation: 'float 3s ease-in-out infinite' }}
            strokeWidth={2.5}
          />
        )}
        <div
          className="rounded-full flex items-center justify-center relative overflow-hidden"
          style={{
            width: avatarSize,
            height: avatarSize,
            background: 'rgba(20,20,20,0.7)',
            boxShadow: `0 0 0 4px ${theme.ring}, 0 0 40px ${theme.glow}, inset 0 0 20px rgba(0,0,0,0.4)`,
          }}
        >
          {entry.user_avatar ? (
            <img
              src={`${BACKEND_URL}${entry.user_avatar}`}
              alt={entry.user_name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className="text-4xl font-black" style={{ color: color.text }}>
              {memberInitials(entry.user_name)}
            </span>
          )}
        </div>
      </div>

      {/* Velocidad media (destacada) */}
      <div className="flex flex-col items-center mb-2">
        <span className="text-3xl sm:text-4xl font-black leading-none" style={{ color: theme.ring, textShadow: `0 0 14px ${theme.glow}` }}>
          {entry.kmh.toFixed(1)}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-gray-300 font-bold">km/h media</span>
      </div>

      {/* Pedestal */}
      <div
        className="w-24 sm:w-28 flex items-center justify-center relative rounded-t-md shadow-2xl"
        style={{
          height: pedestalHeight,
          background: theme.pedestalGrad,
          boxShadow: `0 -4px 30px ${theme.glow}, inset 0 2px 0 rgba(255,255,255,0.4), inset 0 -8px 16px rgba(0,0,0,0.3)`,
        }}
      >
        {/* Highlight superior */}
        <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-b from-white/40 to-transparent rounded-t-md" />

        {/* Solo el número grande + medalla */}
        <div className="flex flex-col items-center gap-1">
          <Icon className="w-5 h-5" style={{ color: theme.pedestalText }} strokeWidth={3} />
          <span className="text-5xl sm:text-6xl font-black" style={{ color: theme.pedestalText, opacity: 0.85, lineHeight: 1 }}>
            {theme.label}
          </span>
          <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: theme.pedestalText, opacity: 0.75 }}>
            {theme.name}
          </span>
        </div>
      </div>

      {/* Stats en tarjeta oscura debajo (bien legibles) */}
      <div
        className="w-24 sm:w-28 -mt-px flex flex-col items-center gap-1 py-2 px-1 rounded-b-md"
        style={{
          background: 'rgba(0,0,0,0.75)',
          borderTop: `1px solid ${theme.ring}66`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}
      >
        <div className="flex items-baseline gap-1">
          <span className="text-xs font-black text-white tabular-nums">{Number(entry.km).toFixed(1)}</span>
          <span className="text-[8px] uppercase text-gray-400 font-bold">km</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-xs font-black text-white tabular-nums">{formatSegundos(entry.segundos)}</span>
        </div>
      </div>
    </div>
  );
}

function PodiumOlympic({ entries }) {
  if (entries.length === 0) return null;
  // Orden visual: 2º, 1º, 3º (1º en el centro)
  const left = entries[1];
  const center = entries[0];
  const right = entries[2];
  return (
    <div className="relative" data-testid="reto-podium">
      <div className="absolute inset-0 bg-gradient-radial from-yellow-500/10 via-transparent to-transparent pointer-events-none" />
      <div className="flex items-end justify-center gap-2 sm:gap-4 px-2 pt-10 pb-2">
        {left ? <PodiumEntry entry={left} place={1} /> : <div className="w-24 sm:w-28" />}
        {center && <PodiumEntry entry={center} place={0} />}
        {right ? <PodiumEntry entry={right} place={2} /> : <div className="w-24 sm:w-28" />}
      </div>
      {/* Base sombreada del pedestal */}
      <div className="h-2 mx-4 bg-black/40 rounded-full blur-md" />
      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(-50%, 0); }
          50% { transform: translate(-50%, -4px); }
        }
      `}</style>
    </div>
  );
}

function RankingRow({ entry, place }) {
  const color = memberColor(entry.user_id);
  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-900/60 border border-gray-800 hover:border-gray-700 transition-colors"
      style={{ borderLeft: `3px solid ${color.ring}` }}
      data-testid={`ranking-row-${place + 1}`}
    >
      <div className="text-sm font-bold text-gray-500 w-6 text-center">{place + 1}</div>
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: 'rgba(20,20,20,0.6)', boxShadow: `0 0 0 2px ${color.ring}` }}
      >
        {entry.user_avatar ? (
          <img src={`${BACKEND_URL}${entry.user_avatar}`} alt={entry.user_name} className="w-full h-full rounded-full object-cover" />
        ) : (
          <span className="text-xs font-bold" style={{ color: color.text }}>{memberInitials(entry.user_name)}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-semibold text-white truncate" style={{ color: color.text }}>{entry.user_name}</p>
          {entry.user_sexo === 'M' && <Mars className="w-3 h-3 text-blue-400" />}
          {entry.user_sexo === 'F' && <Venus className="w-3 h-3 text-pink-400" />}
        </div>
        <p className="text-[10px] text-gray-400">{formatKm(entry.km)} · {formatSegundos(entry.segundos)}</p>
      </div>
      <div className="flex flex-col items-end">
        <span className="text-base font-bold text-gold leading-none tabular-nums">{entry.kmh.toFixed(1)}</span>
        <span className="text-[9px] text-gray-500 uppercase tracking-wider">km/h media</span>
      </div>
    </div>
  );
}

function RetoCard({ reto, onClick, featured }) {
  return (
    <button
      onClick={onClick}
      className={`text-left w-full rounded-2xl border transition-all group ${
        featured
          ? 'bg-gradient-to-br from-emerald-900/30 via-cyan-900/20 to-emerald-900/30 border-emerald-500/40 hover:border-emerald-400 shadow-lg shadow-emerald-900/20 p-5'
          : 'bg-gray-900/60 border-gray-800 hover:border-gold/40 p-4'
      }`}
      data-testid={`reto-card-${reto.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`rounded-xl flex items-center justify-center flex-shrink-0 ${
            featured ? 'w-14 h-14 bg-emerald-500/20' : 'w-10 h-10 bg-gold/10'
          }`}
        >
          <Bike className={featured ? 'w-7 h-7 text-emerald-300' : 'w-5 h-5 text-gold'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`font-bold truncate ${featured ? 'text-xl text-white' : 'text-base text-white'}`}>
              {reto.nombre}
            </h3>
            {featured && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/50 text-emerald-200 text-[10px] font-bold uppercase tracking-wider">
                Reto destacado
              </span>
            )}
          </div>
          {reto.descripcion && (
            <p className={`text-gray-400 mt-1 ${featured ? 'text-sm' : 'text-xs'}`}>{reto.descripcion}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-3 text-xs">
            <span className="flex items-center gap-1 text-gray-300">
              <Users className="w-3.5 h-3.5 text-cyan-400" />
              <b>{reto.participantes || 0}</b> participantes
            </span>
            {reto.mejor_kmh != null && (
              <span className="flex items-center gap-1 text-gray-300">
                <Gauge className="w-3.5 h-3.5 text-gold" />
                Top <b>{reto.mejor_kmh.toFixed(1)}</b> km/h
              </span>
            )}
            {reto.max_km > 0 && (
              <span className="flex items-center gap-1 text-gray-300">
                <Target className="w-3.5 h-3.5 text-emerald-400" />
                Récord <b>{reto.max_km.toFixed(1)}</b> km
              </span>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 text-gold opacity-50 group-hover:opacity-100 transition-opacity">
          <TrendingUp className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
}

const TABS = [
  { key: 'general', label: 'General', icon: Trophy, color: 'text-gold', activeBg: 'bg-gold/20 border-gold text-gold' },
  { key: 'M', label: 'Masculino', icon: Mars, color: 'text-blue-400', activeBg: 'bg-blue-500/20 border-blue-400 text-blue-300' },
  { key: 'F', label: 'Femenino', icon: Venus, color: 'text-pink-400', activeBg: 'bg-pink-500/20 border-pink-400 text-pink-300' },
];

function RetoDetail({ retoId, onBack }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('general');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/retos/${retoId}/ranking`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [retoId]);

  useEffect(() => { load(); }, [load]);

  // Ranking filtrado y reordenado por tab
  const filteredRanking = useMemo(() => {
    if (!data) return [];
    if (tab === 'general') return data.ranking;
    return data.ranking.filter(r => r.user_sexo === tab);
  }, [data, tab]);

  // Conteos para los tabs
  const counts = useMemo(() => {
    if (!data) return { general: 0, M: 0, F: 0 };
    const M = data.ranking.filter(r => r.user_sexo === 'M').length;
    const F = data.ranking.filter(r => r.user_sexo === 'F').length;
    return { general: data.ranking.length, M, F };
  }, [data]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">Cargando ranking...</p>
      </div>
    );
  }

  if (!data) return null;

  const { reto } = data;
  const podio = filteredRanking.slice(0, 3);
  const resto = filteredRanking.slice(3);
  const myEntry = filteredRanking.find(r => r.user_id === user?.id);
  const myPlace = myEntry ? filteredRanking.findIndex(r => r.user_id === user?.id) : -1;

  return (
    <div data-testid={`reto-detail-${retoId}`}>
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-gray-300 hover:text-gold transition-colors text-sm mb-4"
        data-testid="reto-back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver a la lista
      </button>

      {/* Header del reto */}
      <div className="bg-gradient-to-br from-emerald-900/40 via-cyan-900/20 to-transparent rounded-2xl border border-emerald-500/30 p-5 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <Bike className="w-7 h-7 text-emerald-300" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white">{reto.nombre}</h1>
            {reto.descripcion && <p className="text-sm text-gray-300 mt-1">{reto.descripcion}</p>}
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-400 flex-wrap">
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {counts.general} participantes</span>
              <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-gold" /> Más km/h gana</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs de clasificación */}
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar pb-1" data-testid="reto-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const c = counts[t.key];
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap transition-all ${
                active
                  ? t.activeBg
                  : 'bg-gray-900/40 border-gray-700 text-gray-400 hover:border-gray-600'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-white/10' : 'bg-gray-800'}`}>
                {c}
              </span>
            </button>
          );
        })}
      </div>

      {filteredRanking.length === 0 ? (
        <div className="bg-gray-900/40 rounded-xl border border-dashed border-gray-700 p-8 text-center">
          <Trophy className="w-10 h-10 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">
            {tab === 'general'
              ? 'Aún no hay registros. ¡Sé el primero en pedalear!'
              : `Aún no hay participantes en categoría ${tab === 'M' ? 'masculina' : 'femenina'}. Configura tu sexo en el perfil para aparecer aquí.`}
          </p>
        </div>
      ) : (
        <>
          {/* Podio olimpiada */}
          {podio.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1 px-1">
                <Trophy className="w-4 h-4 text-gold" />
                <span className="text-xs uppercase tracking-widest text-gold font-bold">
                  Podio {tab !== 'general' ? (tab === 'M' ? 'Masculino' : 'Femenino') : ''}
                </span>
              </div>
              <div className="bg-gradient-to-b from-black/40 via-gray-900/60 to-black/60 rounded-2xl border border-gold/20 overflow-hidden">
                <PodiumOlympic entries={podio} />
              </div>
            </div>
          )}

          {/* Mi posición (si no estoy en el podio) */}
          {myEntry && myPlace > 2 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-cyan-400" />
                <span className="text-xs uppercase tracking-wider text-cyan-300 font-bold">Tu posición</span>
              </div>
              <div className="border border-cyan-500/40 rounded-xl bg-cyan-900/10">
                <RankingRow entry={myEntry} place={myPlace} />
              </div>
            </div>
          )}

          {/* Resto del ranking */}
          {resto.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Medal className="w-4 h-4 text-gray-400" />
                <span className="text-xs uppercase tracking-wider text-gray-400 font-bold">Ranking completo</span>
              </div>
              <div className="space-y-2">
                {resto.map((e, idx) => <RankingRow key={e.user_id} entry={e} place={idx + 3} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function RetosKlan() {
  const navigate = useNavigate();
  const [retos, setRetos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReto, setSelectedReto] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/retos`);
        setRetos(res.data);
      } catch (e) {
        console.error(e);
      } finally { setLoading(false); }
    })();
  }, []);

  // Reto destacado: "Bici matadora" o el primero con más participantes
  const featured = retos.find(r => /bici\s*matadora/i.test(r.nombre)) || retos[0];
  const rest = retos.filter(r => !featured || r.id !== featured.id);

  return (
    <div className="min-h-screen bg-dark-mk pb-24" data-testid="retos-klan-page">
      <div className="sticky top-0 z-30 bg-dark-mk/90 backdrop-blur-md border-b border-gray-800/50 px-4 pt-4 pb-3">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-gray-300 hover:text-gold transition-colors text-sm"
            data-testid="retos-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Volver</span>
          </button>
          <h1 className="text-xl font-bold text-white flex items-center gap-2 flex-1">
            <Bike className="w-5 h-5 text-emerald-300" /> Retos del Klan
          </h1>
        </div>
        <p className="text-[11px] text-gray-400">Compite por la mejor velocidad media. Más km en menos tiempo = más arriba en el ranking.</p>
      </div>

      <div className="px-4 pt-4 max-w-2xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Cargando retos...</p>
          </div>
        ) : selectedReto ? (
          <RetoDetail retoId={selectedReto} onBack={() => setSelectedReto(null)} />
        ) : retos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
            <Bike className="w-12 h-12 text-gray-600" />
            <p className="text-sm text-center max-w-xs">
              Aún no hay retos creados. El entrenador puede crear un reto añadiendo un entreno de tipo <b>Distancia + Tiempo</b> en el Hall of Fame (ej: &quot;Bici matadora&quot;).
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {featured && (
              <RetoCard reto={featured} featured onClick={() => setSelectedReto(featured.id)} />
            )}
            {rest.length > 0 && (
              <div className="pt-2">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 px-1">Otros retos</p>
                <div className="space-y-2">
                  {rest.map(r => (
                    <RetoCard key={r.id} reto={r} onClick={() => setSelectedReto(r.id)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
